// /festive-orders — Festive Orders Admin (PAGES.md §3.67)
//
// Same as POS festive orders (§2.26) with admin-level filters, LocationSwitcher,
// and reporting. Manage pre-booked festive/seasonal orders.
// RESTAURANT vertical only — MANAGER+ (level 30). Feature flag: ENABLE_FESTIVE_MODULE.
// Traceability: FR-FEST-001 to FR-FEST-013

import { getSubunitMultiplier } from '@bbpos/utils';
import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  PageContainer,
  Button,
  Badge,
  Select,
  Skeleton,
  EmptyState,
  ErrorBoundary,
  StatCard,
  DataTable,
  Pagination,
  Modal,
  Input,
  TextArea,
  useToast,
} from '@bbpos/ui';
import type { ColumnDef } from '@bbpos/ui';
import { festiveService } from '@/services/festive.service';
import type {
  Festival,
  FestiveOrder,
  FestiveOrderStatus,
  CancelFestiveOrderDto,
  CreateFestiveOrderDto,
} from '@/services/festive.service';
import { useDashboardStore } from '@/stores/use-dashboard-store';
import { useAuthStore } from '@/stores/use-auth-store';
import { GLASS, GLASS_FILTER, GLASS_INTERACTIVE, cn } from '@/constants/design-tokens';
import { formatPaise } from '@/utils/currency';
import { useTenantCurrency } from '@/hooks/use-tenant-currency';
import { useTenantDial } from '@/hooks/use-tenant-dial';
import { ApiError } from '@/lib/api-client';
import {
  ROLE_LEVELS,
  ORDER_STATUS_BADGE as STATUS_BADGE,
  ORDER_STATUS_FILTER_OPTIONS as STATUS_FILTER_OPTIONS,
  CANCELLATION_REASONS,
  REFUND_METHODS,
  PAGE_SIZE,
  formatDate,
} from './constants';
import {
  PlusIcon, GiftIcon, GiftEmptyIcon, CurrencyIcon,
  CalendarIcon, PackageIcon,
} from './icons';

// ─── Mobile Order Card ──────────────────────────────────────────────────────

function OrderMobileCard({ order }: { order: FestiveOrder }) {
  const currency = useTenantCurrency();
  const badge = STATUS_BADGE[order.status] ?? STATUS_BADGE.BOOKED;
  return (
    <div className={cn(GLASS_INTERACTIVE, 'p-4 animate-slide-up')}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="font-bold text-neutral-900 dark:text-neutral-100 truncate">{order.customer_name}</p>
          <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5 font-mono">
            {order.customer_phone}
          </p>
        </div>
        <Badge variant={badge.variant} size="sm">{badge.label}</Badge>
      </div>
      <div className="flex items-center justify-between mt-3">
        <span className="text-sm font-bold font-mono tabular-nums text-neutral-900 dark:text-neutral-100">
          {formatPaise(order.total, currency)}
        </span>
        <span className="text-xs font-mono tabular-nums text-neutral-500 dark:text-neutral-400">
          {formatDate(order.delivery_date)} · {order.items.length} items
        </span>
      </div>
      {order.deposit_paid && (
        <div className="mt-2">
          <Badge variant="success" size="sm">Deposit Paid: {formatPaise(order.deposit_amount, currency)}</Badge>
        </div>
      )}
    </div>
  );
}

// ─── Component ──────────────────────────────────────────────────────────────

function FestiveOrdersContent() {
  const dial = useTenantDial();
  const { toast } = useToast();
  const selectedLocationId = useDashboardStore((s) => s.selectedLocationId);
  const profile = useAuthStore((s) => s.profile);
  const roleLevel = ROLE_LEVELS[profile?.role ?? ''] ?? 0;
  const canCancel = roleLevel >= 30; // MANAGER+
  const currency = useTenantCurrency();

  const [searchParams, setSearchParams] = useSearchParams();
  const setSearchParamsRef = useRef(setSearchParams);
  setSearchParamsRef.current = setSearchParams;

  const urlStatus = searchParams.get('status') ?? '';
  const urlFestival = searchParams.get('festival') ?? '';
  const urlPage = Number(searchParams.get('page') ?? '1');

  const [statusFilter, setStatusFilter] = useState(urlStatus);
  const [festivalFilter, setFestivalFilter] = useState(urlFestival);
  const [page, setPage] = useState(urlPage);

  useEffect(() => {
    const params: Record<string, string> = {};
    if (statusFilter) params.status = statusFilter;
    if (festivalFilter) params.festival = festivalFilter;
    if (page > 1) params.page = String(page);
    setSearchParamsRef.current(params, { replace: true });
  }, [statusFilter, festivalFilter, page]);

  // ── Data ────────────────────────────────────────────────────────────────
  const [orders, setOrders] = useState<FestiveOrder[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [confirmedCount, setConfirmedCount] = useState(0);
  const [festivals, setFestivals] = useState<Festival[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // ── Cancel modal ────────────────────────────────────────────────────────
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [cancelOrderId, setCancelOrderId] = useState<string | null>(null);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelNotes, setCancelNotes] = useState('');
  const [cancelRefundMethod, setCancelRefundMethod] = useState('');
  const [isCancelling, setIsCancelling] = useState(false);

  // ── Create-order modal ────────────────────────────────────────────────────
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [createFestivalId, setCreateFestivalId] = useState('');
  const [createFestival, setCreateFestival] = useState<Festival | null>(null);
  const [custName, setCustName] = useState('');
  const [custPhone, setCustPhone] = useState('');
  const [orderType, setOrderType] = useState<'PICKUP' | 'DELIVERY' | 'DINE_IN'>('PICKUP');
  const [pickupDate, setPickupDate] = useState('');
  const [pickupTime, setPickupTime] = useState('');
  const [deliveryAddress, setDeliveryAddress] = useState('');
  const [advanceRupees, setAdvanceRupees] = useState('');
  const [paymentMethod, setPaymentMethod] = useState<'CASH' | 'UPI' | 'CARD'>('CASH');
  const [specialInstructions, setSpecialInstructions] = useState('');
  const [itemQty, setItemQty] = useState<Record<string, string>>({});
  const [isCreating, setIsCreating] = useState(false);

  // ── Fetch festivals for filter ──────────────────────────────────────────
  useEffect(() => {
    festiveService.listFestivals().then((r) => setFestivals(r.data)).catch(() => { /* silent — festival filter stays empty */ });
  }, []);

  const festivalOptions = useMemo(() => [
    { value: '', label: 'All Festivals' },
    ...festivals.map((f) => ({ value: f.id, label: f.name })),
  ], [festivals]);

  // ── Fetch orders ────────────────────────────────────────────────────────
  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const filters = {
        festival_id: festivalFilter || undefined,
        status: (statusFilter as FestiveOrderStatus) || undefined,
        location_id: selectedLocationId ?? undefined,
      };
      const [result, stats] = await Promise.all([
        festiveService.listFestiveOrders({ page, limit: PAGE_SIZE, ...filters }),
        festiveService.getFestiveOrderStats(filters),
      ]);
      setOrders(result.data);
      setTotalItems(result.meta.total);
      setConfirmedCount(stats.confirmed_count);
    } catch (err) {
      toast({ title: err instanceof ApiError ? err.message : 'Failed to load orders.', variant: 'error' });
    } finally {
      setIsLoading(false);
    }
  }, [page, festivalFilter, statusFilter, selectedLocationId, toast]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ── Status update ─────────────────────────────────────────────────────
  const handleStatusUpdate = useCallback(async (orderId: string, status: FestiveOrderStatus) => {
    try {
      await festiveService.updateFestiveOrderStatus(orderId, status);
      toast({ title: `Order status updated to ${status}`, variant: 'success' });
      fetchData();
    } catch (err) {
      toast({ title: err instanceof ApiError ? err.message : 'Failed to update status.', variant: 'error' });
    }
  }, [fetchData, toast]);

  // ── Cancel order ────────────────────────────────────────────────────────
  const openCancelModal = useCallback((orderId: string) => {
    setCancelOrderId(orderId);
    setCancelReason('');
    setCancelNotes('');
    setCancelRefundMethod('');
    setCancelModalOpen(true);
  }, []);

  const handleCancel = useCallback(async () => {
    if (!cancelOrderId || !cancelReason || !cancelRefundMethod) return;
    setIsCancelling(true);
    try {
      const dto: CancelFestiveOrderDto = {
        reason: cancelReason,
        reason_notes: cancelNotes.trim() || undefined,
        refund_method: cancelRefundMethod as CancelFestiveOrderDto['refund_method'],
      };
      await festiveService.cancelFestiveOrder(cancelOrderId, dto);
      toast({ title: 'Festive order cancelled', variant: 'success' });
      setCancelModalOpen(false);
      fetchData();
    } catch (err) {
      toast({ title: err instanceof ApiError ? err.message : 'Failed to cancel order.', variant: 'error' });
    } finally {
      setIsCancelling(false);
    }
  }, [cancelOrderId, cancelReason, cancelNotes, cancelRefundMethod, fetchData, toast]);

  // ── Create order ──────────────────────────────────────────────────────────
  const openCreateModal = useCallback(() => {
    setCreateFestivalId('');
    setCreateFestival(null);
    setCustName('');
    setCustPhone('');
    setOrderType('PICKUP');
    setPickupDate('');
    setPickupTime('');
    setDeliveryAddress('');
    setAdvanceRupees('');
    setPaymentMethod('CASH');
    setSpecialInstructions('');
    setItemQty({});
    setCreateModalOpen(true);
  }, []);

  // Load the selected festival's items so the operator can pick products.
  useEffect(() => {
    if (!createFestivalId) { setCreateFestival(null); return; }
    let active = true;
    festiveService.getFestival(createFestivalId)
      .then((f) => { if (active) { setCreateFestival(f); setItemQty({}); } })
      .catch(() => { if (active) setCreateFestival(null); });
    return () => { active = false; };
  }, [createFestivalId]);

  const createItems = useMemo(
    () => Object.entries(itemQty)
      .map(([product_id, q]) => ({ product_id, quantity: Number(q) }))
      .filter((i) => Number.isFinite(i.quantity) && i.quantity > 0),
    [itemQty],
  );

  const canSubmitCreate =
    !!createFestivalId &&
    !!custName.trim() &&
    /^\+[1-9]\d{6,14}$/.test(custPhone) &&
    !!pickupDate &&
    createItems.length > 0 &&
    !!selectedLocationId;

  const handleCreate = useCallback(async () => {
    if (!selectedLocationId) {
      toast({ title: 'Select a specific location before creating an order.', variant: 'error' });
      return;
    }
    if (!canSubmitCreate) return;
    setIsCreating(true);
    try {
      const dto: CreateFestiveOrderDto = {
        festival_id: createFestivalId,
        customer_name: custName.trim(),
        customer_phone: custPhone.trim(),
        order_type: orderType,
        pickup_date: pickupDate,
        pickup_time: pickupTime.trim(),
        delivery_address: orderType === 'DELIVERY' ? (deliveryAddress.trim() || undefined) : undefined,
        items: createItems,
        advance_amount: advanceRupees ? Math.round(Number(advanceRupees) * getSubunitMultiplier(currency)) : 0,
        payment_method: paymentMethod,
        special_instructions: specialInstructions.trim() || undefined,
        location_id: selectedLocationId,
      };
      await festiveService.createFestiveOrder(dto);
      toast({ title: 'Festive order created', variant: 'success' });
      setCreateModalOpen(false);
      fetchData();
    } catch (err) {
      toast({ title: err instanceof ApiError ? err.message : 'Failed to create order.', variant: 'error' });
    } finally {
      setIsCreating(false);
    }
  }, [
    selectedLocationId, canSubmitCreate, createFestivalId, custName, custPhone, orderType,
    pickupDate, pickupTime, deliveryAddress, createItems, advanceRupees, paymentMethod,
    specialInstructions, fetchData, toast,
  ]);

  // ── Page title ──────────────────────────────────────────────────────────
  useEffect(() => { document.title = 'Festive Orders — Orio360 Admin'; }, []);

  // ── Keyboard shortcuts ──────────────────────────────────────────────────
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) return;
      if (e.key === 'n') { e.preventDefault(); openCreateModal(); }
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [openCreateModal]);

  // ── Table columns ─────────────────────────────────────────────────────
  const columns: ColumnDef<FestiveOrder>[] = useMemo(() => [
    {
      key: 'customer_name', header: 'Customer',
      render: (_v: unknown, row: FestiveOrder) => (
        <div>
          <span className="font-bold text-neutral-900 dark:text-neutral-100">{row.customer_name}</span>
          <p className="text-xs text-neutral-500 dark:text-neutral-400 font-mono">{row.customer_phone}</p>
        </div>
      ),
    },
    {
      key: 'festival_name', header: 'Festival',
      render: (_v: unknown, row: FestiveOrder) => (
        <span className="text-sm text-neutral-700 dark:text-neutral-300">{row.festival_name}</span>
      ),
    },
    {
      key: 'items', header: 'Items',
      render: (_v: unknown, row: FestiveOrder) => (
        <span className="text-sm text-neutral-700 dark:text-neutral-300 font-mono tabular-nums">
          {row.items.length} item{row.items.length !== 1 ? 's' : ''}
        </span>
      ),
    },
    {
      key: 'total', header: 'Amount', numeric: true,
      render: (_v: unknown, row: FestiveOrder) => (
        <span className="font-mono tabular-nums font-bold text-neutral-900 dark:text-neutral-100">
          {formatPaise(row.total, currency)}
        </span>
      ),
    },
    {
      key: 'delivery_date', header: 'Delivery',
      render: (_v: unknown, row: FestiveOrder) => (
        <span className="text-sm font-mono tabular-nums text-neutral-700 dark:text-neutral-300">
          {formatDate(row.delivery_date)}
        </span>
      ),
    },
    {
      key: 'status', header: 'Status',
      render: (_v: unknown, row: FestiveOrder) => {
        const badge = STATUS_BADGE[row.status] ?? STATUS_BADGE.BOOKED;
        return <Badge variant={badge.variant} size="sm">{badge.label}</Badge>;
      },
    },
    {
      key: 'actions', header: '',
      render: (_v: unknown, row: FestiveOrder) => {
        if (row.status === 'CANCELLED' || row.status === 'DELIVERED') return null;
        return (
          <div className="flex items-center gap-2">
            {/* Festive orders follow the DB lifecycle CONFIRMED → READY → DELIVERED.
                "Start" marks the order ready for pickup; "Deliver" completes it. */}
            {row.status === 'CONFIRMED' && (
              <Button variant="secondary" size="sm" onClick={() => handleStatusUpdate(row.id, 'READY')}>
                Start
              </Button>
            )}
            {row.status === 'READY' && (
              <Button variant="secondary" size="sm" onClick={() => handleStatusUpdate(row.id, 'DELIVERED')}>
                Deliver
              </Button>
            )}
            {canCancel && (
              <Button variant="danger" size="sm" onClick={() => openCancelModal(row.id)}>
                Cancel
              </Button>
            )}
          </div>
        );
      },
    },
  ], [handleStatusUpdate, canCancel, openCancelModal, currency]);

  const totalPages = Math.ceil(totalItems / PAGE_SIZE);

  return (
    <PageContainer
      title="Festive Orders"
      subtitle="Manage pre-booked festive and seasonal orders"
      breadcrumbs={[
        { label: 'Home', href: '/home' },
        { label: 'Festive Orders' },
      ]}
      actions={
        <div className="hidden sm:block">
          <Button variant="primary" size="sm" onClick={openCreateModal}>
            <PlusIcon /><span className="ml-1.5">New Festive Order</span>
          </Button>
        </div>
      }
    >
      <div className="animate-fade-in">
        {/* ═══ Active Festival Banner (spec §3.67: same as POS §2.26) ═══ */}
        {festivals.find((f) => f.is_active) && (() => {
          const active = festivals.find((f) => f.is_active)!;
          return (
            <div className="bg-gradient-to-r from-violet-600 to-pink-600 rounded-2xl p-5 text-white shadow-lg mb-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                  <h2 className="text-xl font-bold text-white">Festive Orders</h2>
                  <p className="text-violet-100 text-sm mt-0.5">
                    {active.name} · Booking deadline: {formatDate(active.booking_deadline)}
                  </p>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-center">
                    <p className="text-2xl font-bold font-mono tabular-nums">
                      {active.order_count.toLocaleString('en-IN')}
                    </p>
                    <p className="text-xs text-violet-200">orders</p>
                  </div>
                  {active.total_capacity && (
                    <div className="text-center">
                      <p className="text-2xl font-bold font-mono tabular-nums">
                        {active.booked_count}/{active.total_capacity}
                      </p>
                      <p className="text-xs text-violet-200">capacity</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })()}

        {/* ═══ Filters ═══ */}
        <div className={cn(GLASS_FILTER, 'p-4 mb-6')}>
          <div className="flex flex-wrap items-center gap-3">
            <div className="w-44">
              <Select
                options={festivalOptions}
                value={festivalFilter}
                onChange={(val) => { setFestivalFilter(typeof val === 'string' ? val : val[0] ?? ''); setPage(1); }}
                placeholder="All Festivals"
                clearable
              />
            </div>
            <div className="w-44">
              <Select
                options={STATUS_FILTER_OPTIONS}
                value={statusFilter}
                onChange={(val) => { setStatusFilter(typeof val === 'string' ? val : val[0] ?? ''); setPage(1); }}
                placeholder="All Statuses"
                clearable
              />
            </div>
          </div>
        </div>

        {isLoading ? (
          <div className="space-y-6">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="animate-slide-up" style={{ animationDelay: `${i * 50}ms` }}>
                  <StatCard title="" value="" loading />
                </div>
              ))}
            </div>
            <div className={cn(GLASS, 'p-6 space-y-4')}>
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="flex gap-4">
                  <Skeleton variant="text" width="20%" height={18} />
                  <Skeleton variant="text" width="15%" height={18} />
                  <Skeleton variant="text" width="8%" height={18} />
                  <Skeleton variant="text" width="12%" height={18} />
                  <Skeleton variant="text" width="15%" height={18} />
                  <Skeleton variant="rect" width={70} height={24} />
                </div>
              ))}
            </div>
          </div>
        ) : orders.length === 0 ? (
          <EmptyState
            variant="page"
            icon={<GiftEmptyIcon />}
            title="No festive orders"
            description="Pre-book orders for upcoming festivals"
            action={{ label: 'New Festive Order', onClick: openCreateModal }}
          />
        ) : (
          <>
            {/* ═══ KPI Cards ═══ */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <div className="animate-slide-up">
                <StatCard title="Total Orders" value={totalItems.toLocaleString('en-IN')} icon={<GiftIcon className="h-4 w-4" />} />
              </div>
              <div className="animate-slide-up" style={{ animationDelay: '50ms' }}>
                <StatCard title="Page Revenue" value={formatPaise(orders.reduce((s, o) => s + o.total, 0), currency)} icon={<CurrencyIcon className="h-4 w-4" />} />
              </div>
              <div className="animate-slide-up" style={{ animationDelay: '100ms' }}>
                <StatCard title="Confirmed" value={String(confirmedCount)} icon={<PackageIcon className="h-4 w-4" />} />
              </div>
              <div className="animate-slide-up" style={{ animationDelay: '150ms' }}>
                <StatCard title="Festivals" value={String(festivals.length)} icon={<CalendarIcon className="h-4 w-4" />} />
              </div>
            </div>

            {/* ═══ Desktop DataTable ═══ */}
            <div className={cn(GLASS, 'overflow-hidden hidden sm:block mb-6')}>
              <div className="px-5 py-4">
                <h3 className="text-sm font-bold text-neutral-700 dark:text-neutral-300 uppercase tracking-wider">
                  Orders ({totalItems})
                </h3>
              </div>
              <DataTable<FestiveOrder> columns={columns} data={orders} />
            </div>

            {/* ═══ Mobile Cards ═══ */}
            <div className="block sm:hidden space-y-3 mb-6">
              <h3 className="text-sm font-bold text-neutral-700 dark:text-neutral-300 uppercase tracking-wider px-1">
                Orders ({totalItems})
              </h3>
              {orders.map((order) => (
                <OrderMobileCard key={order.id} order={order} />
              ))}
            </div>

            {totalPages > 1 && (
              <div className="mb-6">
                <Pagination
                  currentPage={page}
                  totalPages={totalPages}
                  totalItems={totalItems}
                  pageSize={PAGE_SIZE}
                  onPageChange={setPage}
                />
              </div>
            )}
          </>
        )}
      </div>

      {/* ═══ Create Order Modal ═══ */}
      <Modal open={createModalOpen} onClose={() => setCreateModalOpen(false)} title="New Festive Order">
        <div className="space-y-4 p-1">
          {!selectedLocationId && (
            <div className="text-xs text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20 px-3 py-2 rounded-lg">
              Select a specific location (not “All Locations”) to book an order.
            </div>
          )}

          <div>
            <label className="text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1 block">
              Festival *
            </label>
            <Select
              options={festivals.map((f) => ({ value: f.id, label: f.name }))}
              value={createFestivalId}
              onChange={(val) => setCreateFestivalId(typeof val === 'string' ? val : val[0] ?? '')}
              placeholder="Select festival"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Input label="Customer Name" required value={custName} onChange={setCustName} placeholder="e.g. Priya Sharma" />
            <Input label="Phone" required variant="phone" defaultDial={dial} value={custPhone} onChange={setCustPhone} />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1 block">
                Order Type *
              </label>
              <Select
                options={[
                  { value: 'PICKUP', label: 'Pickup' },
                  { value: 'DELIVERY', label: 'Delivery' },
                  { value: 'DINE_IN', label: 'Dine In' },
                ]}
                value={orderType}
                onChange={(val) => setOrderType((typeof val === 'string' ? val : val[0]) as typeof orderType)}
              />
            </div>
            <Input label="Delivery / Pickup Date" required type="date" value={pickupDate} onChange={setPickupDate} />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Input label="Time Slot" value={pickupTime} onChange={setPickupTime} placeholder="e.g. 10:00-12:00" />
            {orderType === 'DELIVERY' && (
              <Input label="Delivery Address" value={deliveryAddress} onChange={setDeliveryAddress} placeholder="Address" />
            )}
          </div>

          <div>
            <label className="text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1 block">
              Items *
            </label>
            {!createFestivalId ? (
              <p className="text-xs text-neutral-400 dark:text-neutral-500">Select a festival to choose items.</p>
            ) : (createFestival?.items.length ?? 0) === 0 ? (
              <p className="text-xs text-neutral-400 dark:text-neutral-500">This festival has no items configured.</p>
            ) : (
              <div className="space-y-2 max-h-48 overflow-auto">
                {createFestival!.items.map((item) => (
                  <div key={item.product_id} className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm text-neutral-800 dark:text-neutral-200 truncate">{item.product_name}</p>
                      <p className="text-xs text-neutral-500 dark:text-neutral-400 font-mono">{formatPaise(item.price, currency)}</p>
                    </div>
                    <div className="w-28 shrink-0">
                      <Input
                        variant="quantity"
                        min={0}
                        max={item.max_quantity ?? undefined}
                        value={itemQty[item.product_id] ?? '0'}
                        onChange={(val) => setItemQty((prev) => ({ ...prev, [item.product_id]: val }))}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Input label="Advance Amount" variant="currency" currency={currency} value={advanceRupees} onChange={setAdvanceRupees} placeholder="0" />
            <div>
              <label className="text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1 block">
                Payment Method
              </label>
              <Select
                options={[
                  { value: 'CASH', label: 'Cash' },
                  { value: 'UPI', label: 'UPI' },
                  { value: 'CARD', label: 'Card' },
                ]}
                value={paymentMethod}
                onChange={(val) => setPaymentMethod((typeof val === 'string' ? val : val[0]) as typeof paymentMethod)}
              />
            </div>
          </div>

          <div>
            <label className="text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1 block">
              Special Instructions
            </label>
            <TextArea value={specialInstructions} onChange={(val) => setSpecialInstructions(val)} placeholder="Any additional details..." rows={2} />
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <Button variant="secondary" size="sm" onClick={() => setCreateModalOpen(false)} disabled={isCreating}>
              Cancel
            </Button>
            <Button variant="primary" size="sm" onClick={handleCreate} disabled={isCreating || !canSubmitCreate}>
              {isCreating ? 'Creating...' : 'Create Order'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* ═══ Cancel Order Modal ═══ */}
      <Modal open={cancelModalOpen} onClose={() => setCancelModalOpen(false)} title="Cancel Festive Order">
        <div className="space-y-4 p-1">
          <div className="text-xs text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20 px-3 py-2 rounded-lg">
            Cancellation policy: &gt;48h: full refund, 24-48h: 50% refund, &lt;24h: no refund
          </div>

          <div>
            <label className="text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1 block">
              Cancellation Reason *
            </label>
            <Select
              options={CANCELLATION_REASONS}
              value={cancelReason}
              onChange={(val) => setCancelReason(typeof val === 'string' ? val : val[0] ?? '')}
              placeholder="Select reason"
            />
          </div>

          <div>
            <label className="text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1 block">
              Additional Notes
            </label>
            <TextArea
              value={cancelNotes}
              onChange={(val) => setCancelNotes(val)}
              placeholder="Any additional details..."
              rows={2}
            />
          </div>

          <div>
            <label className="text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1 block">
              Refund Method *
            </label>
            <Select
              options={REFUND_METHODS}
              value={cancelRefundMethod}
              onChange={(val) => setCancelRefundMethod(typeof val === 'string' ? val : val[0] ?? '')}
              placeholder="Select refund method"
            />
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <Button variant="secondary" size="sm" onClick={() => setCancelModalOpen(false)} disabled={isCancelling}>
              Back
            </Button>
            <Button
              variant="danger"
              size="sm"
              onClick={handleCancel}
              disabled={isCancelling || !cancelReason || !cancelRefundMethod}
            >
              {isCancelling ? 'Cancelling...' : 'Confirm Cancellation'}
            </Button>
          </div>
        </div>
      </Modal>
    </PageContainer>
  );
}

export function FestiveOrdersPage() {
  return (
    <ErrorBoundary>
      <FestiveOrdersContent />
    </ErrorBoundary>
  );
}
